import React, { useMemo, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import type { HubBuyingOffer } from 'components/hub/useHubPurchaseOffers';

import { HubFilterDropdown, resolveAvailableFilter } from './HubFilterDropdown';
import { HubSectionHeader } from './HubSectionHeader';
import { isHomePurchaseSectionVisible } from './hubSectionVisibility';
import { TransactionCardSkeleton } from './HubSkeletons';
import { OfferCard } from './OfferCard';
import { buildOfferCardModel } from './offerCardModel';

type OfferFilter = 'active' | 'canceled';

const OFFER_FILTER_OPTIONS: ReadonlyArray<{ value: OfferFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'canceled', label: 'Withdrawn' },
];

interface HomePurchaseSectionProps {
  offers: HubBuyingOffer[];
  isLoading: boolean;
}

export const HomePurchaseSection: React.FC<HomePurchaseSectionProps> = ({ offers, isLoading }) => {
  const [selectedFilter, setSelectedFilter] = useState<OfferFilter | null>(null);

  const { active, canceled } = useMemo(() => {
    const models = offers.map(buildOfferCardModel);
    return {
      active: models.filter((offer) => !offer.isCanceled),
      canceled: models.filter((offer) => offer.isCanceled),
    };
  }, [offers]);

  if (!isHomePurchaseSectionVisible(offers, isLoading)) {
    return null;
  }

  const filter = resolveAvailableFilter(selectedFilter, [
    { value: 'active', available: active.length > 0 },
    { value: 'canceled', available: canceled.length > 0 },
  ]);
  const showFilter = !isLoading && active.length > 0 && canceled.length > 0;
  const visibleOffers = filter === 'active' ? active : canceled;

  return (
    <Col className="w-full gap-4">
      <HubSectionHeader
        title="Home purchase"
        action={
          showFilter ? (
            <HubFilterDropdown
              value={filter}
              options={OFFER_FILTER_OPTIONS}
              onChange={setSelectedFilter}
              ariaLabel="Filter purchase offers"
            />
          ) : undefined
        }
      />

      {isLoading ? (
        <TransactionCardSkeleton />
      ) : (
        visibleOffers.map((offer) => (
          <OfferCard key={offer.id} section="home-purchase" offer={offer} />
        ))
      )}
    </Col>
  );
};
